/**
 * useMasterFilterState — URL-synced filter state for the master
 * dashboard surface. Mirrors the per-project `useBoardFilters` shape
 * (View / kinds / assignees / search) and adds two cross-project
 * dimensions:
 *
 *   - **labels** — filter cards whose `labels[].name` intersects the
 *     selected set. Labels are project-scoped on the BE (different UUIDs
 *     per project) but unify cross-project by NAME, so a single
 *     "Bug" label across three projects shows up as one option here.
 *   - **attendees** — filter event/announcement cards whose
 *     `attendees[].userId` intersects the selected set (events and
 *     announcements use attendees instead of a single assignee).
 *
 * Filter state is held entirely in the URL search params (parsed by
 * `routes/index.tsx` `validateSearch`). Reads come straight from
 * `Route.useSearch()`; writes go through `navigate({ search: ... })`
 * with `replace: true` so the back stack isn't polluted by every
 * keystroke. This makes filtered master views deep-linkable and
 * shareable (matches Jira's `?assignee=...` pattern).
 *
 * The View dropdown is kept (Linear/Notion-style) since all four built-in
 * views (`team-work`, `my-work`, `unassigned`, `review`) translate
 * cleanly to the master surface — `meId` is a global user identity, and
 * the dashboard payload already buckets by canonical column name so
 * "review" maps to `card.columnName === 'Review'` without a per-project
 * status lookup.
 */

import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'

import type { DeliverableKind } from '@/api/types-bridge'
import { Route as MasterRoute } from '@/routes/index'

import type { DashboardCard } from './use-dashboard'

export type MasterViewMode = 'team-work' | 'my-work' | 'unassigned' | 'review'

export const DEFAULT_MASTER_VIEW: MasterViewMode = 'team-work'

export const MASTER_VIEW_LABELS: Record<MasterViewMode, string> = {
  'team-work': 'Team work',
  'my-work': 'My work',
  unassigned: 'Unassigned',
  review: 'Awaiting review',
}

export const MASTER_VIEW_DESCRIPTIONS: Record<MasterViewMode, string> = {
  'team-work': 'Every card across every project.',
  'my-work': 'Cards assigned to you.',
  unassigned: 'Cards with no assignee.',
  review: 'Cards in any project Review column.',
}

export interface MasterFilters {
  view: MasterViewMode
  kinds: readonly DeliverableKind[] | undefined
  /**
   * User ids — same shape as the per-project board's assignee filter,
   * with the literal `'unassigned'` matching cards where
   * `assigneeId == null`. The available list is derived from a UNION of
   * assignee ids that appear on visible cards (the dashboard payload
   * doesn't carry per-project member rosters).
   */
  assignees: readonly string[] | undefined
  /**
   * Label NAMES (not ids). Labels are project-scoped on the BE so the
   * same logical "Bug" label has different UUIDs across projects;
   * unifying by name gives a single cross-project filter option.
   */
  labels: readonly string[] | undefined
  /**
   * Attendee user ids. Drawn from event + announcement
   * `attendees[].userId` arrays union'd across all visible cards.
   */
  attendees: readonly string[] | undefined
  q: string | undefined
}

export interface MasterFilterUpdates {
  view?: MasterViewMode
  kinds?: readonly DeliverableKind[] | undefined
  assignees?: readonly string[] | undefined
  labels?: readonly string[] | undefined
  attendees?: readonly string[] | undefined
  q?: string | undefined
}

export interface UseMasterFilterStateResult {
  filters: MasterFilters
  setFilters: (updates: MasterFilterUpdates) => void
  clearFilters: () => void
  hasActiveFilters: boolean
}

export function useMasterFilterState(): UseMasterFilterStateResult {
  const search = MasterRoute.useSearch()
  // Scoped `from` — the master `layout` search param is a 5-value
  // `MasterLayout`, distinct from the per-project board's 2-value
  // `BoardLayout`. An unscoped `useNavigate()` infers its updater's `prev`
  // as the union of every route's search schema per field name, which
  // broke every `search: (prev) => ({...prev})` spread once the two
  // routes' `layout` types diverged — scoping to `from` narrows `prev`
  // back to this route's own schema. See the identical fix in
  // `src/features/board/use-board-filters.ts`.
  const navigate = useNavigate({ from: '/' })

  const filters = useMemo<MasterFilters>(
    () => ({
      view: search.view ?? DEFAULT_MASTER_VIEW,
      kinds: search.kinds,
      assignees: search.assignees,
      labels: search.labels,
      attendees: search.attendees,
      q: search.q,
    }),
    [search.view, search.kinds, search.assignees, search.labels, search.attendees, search.q],
  )

  const setFilters = useCallback(
    (updates: MasterFilterUpdates) => {
      navigate({
        to: '/',
        search: (prev) => {
          const next = { ...prev }
          if ('view' in updates && updates.view) {
            // Default view is stripped from the URL so a clean `/` stays clean.
            next.view = updates.view === DEFAULT_MASTER_VIEW ? undefined : updates.view
          }
          if ('kinds' in updates) {
            next.kinds = updates.kinds && updates.kinds.length > 0 ? updates.kinds : undefined
          }
          if ('assignees' in updates) {
            next.assignees =
              updates.assignees && updates.assignees.length > 0 ? updates.assignees : undefined
          }
          if ('labels' in updates) {
            next.labels = updates.labels && updates.labels.length > 0 ? updates.labels : undefined
          }
          if ('attendees' in updates) {
            next.attendees =
              updates.attendees && updates.attendees.length > 0 ? updates.attendees : undefined
          }
          if ('q' in updates) {
            next.q = updates.q && updates.q.length > 0 ? updates.q : undefined
          }
          return next
        },
        replace: true,
      })
    },
    [navigate],
  )

  const clearFilters = useCallback(() => {
    navigate({
      to: '/',
      search: (prev) => ({
        // Preserve the deep-link param so clearing filters doesn't
        // close an open card modal.
        selectedDeliverable: prev.selectedDeliverable,
        // Preserve the layout switcher's URL value too, so "Clear
        // filters" on a `?layout=pulse` link doesn't silently bounce
        // the user back to Headers.
        layout: prev.layout,
      }),
      replace: true,
    })
  }, [navigate])

  const hasActiveFilters = useMemo(() => {
    if (filters.view !== DEFAULT_MASTER_VIEW) return true
    if (filters.kinds && filters.kinds.length > 0) return true
    if (filters.assignees && filters.assignees.length > 0) return true
    if (filters.labels && filters.labels.length > 0) return true
    if (filters.attendees && filters.attendees.length > 0) return true
    if (filters.q && filters.q.length > 0) return true
    return false
  }, [filters])

  return { filters, setFilters, clearFilters, hasActiveFilters }
}

/**
 * Pure derivation: applies the active filter set to the dashboard cards
 * array. Identical pipeline to `applyBoardFilters` with two extra
 * stages (labels by name, attendees by userId).
 *
 *   1. Built-in view (team-work / my-work / unassigned / review).
 *   2. Kinds subset.
 *   3. Assignee subset (with `'unassigned'` matching `assigneeId == null`).
 *   4. Label subset (matching `labels[].name`).
 *   5. Attendee subset (matching `attendees[].userId`).
 *   6. Search query (substring on title OR shorthand exact match).
 */
export interface ApplyMasterFiltersArgs {
  cards: readonly DashboardCard[]
  filters: MasterFilters
  /** Current user's id; required for `my-work`. Pass `null` while loading. */
  meId: string | null
}

export function applyMasterFilters({
  cards,
  filters,
  meId,
}: ApplyMasterFiltersArgs): DashboardCard[] {
  let result: DashboardCard[] = [...cards]

  // 1. Built-in view baseline.
  switch (filters.view) {
    case 'my-work': {
      if (meId) {
        result = result.filter((c) => {
          if (c.assigneeId === meId) return true
          // Events + announcements: the user counts as "mine" if they're
          // on the attendee list (matches the per-project board's intent
          // — "things I owe time to / am responsible for").
          if (c.attendees?.some((a) => a.userId === meId)) return true
          return false
        })
      }
      break
    }
    case 'unassigned': {
      // Tasks/tickets with no assignee, plus events/announcements with
      // no attendees (the analogous "no one is on the hook" state).
      result = result.filter((c) => {
        const hasAssignee = c.assigneeId != null
        const hasAttendees = (c.attendees?.length ?? 0) > 0
        return !hasAssignee && !hasAttendees
      })
      break
    }
    case 'review': {
      // Dashboard payload already buckets by canonical column name, so
      // we don't need a per-project status lookup here.
      result = result.filter((c) => c.columnName === 'Review')
      break
    }
    default:
      // 'team-work' (default): no baseline filter.
      break
  }

  // 2. Kinds subset.
  if (filters.kinds && filters.kinds.length > 0) {
    const kindSet = new Set(filters.kinds)
    result = result.filter((c) => kindSet.has(c.kind))
  }

  // 3. Assignees subset.
  if (filters.assignees && filters.assignees.length > 0) {
    const idSet = new Set(filters.assignees)
    const includeUnassigned = idSet.has('unassigned')
    result = result.filter((c) => {
      if (c.assigneeId == null) return includeUnassigned
      return idSet.has(c.assigneeId)
    })
  }

  // 4. Labels subset (matched by name).
  if (filters.labels && filters.labels.length > 0) {
    const nameSet = new Set(filters.labels)
    result = result.filter((c) => {
      const labels = c.labels ?? []
      return labels.some((l) => nameSet.has(l.name))
    })
  }

  // 5. Attendees subset.
  if (filters.attendees && filters.attendees.length > 0) {
    const idSet = new Set(filters.attendees)
    result = result.filter((c) => {
      const attendees = c.attendees ?? []
      return attendees.some((a) => idSet.has(a.userId))
    })
  }

  // 6. Search query.
  if (filters.q && filters.q.length > 0) {
    const q = filters.q.trim()
    const lower = q.toLowerCase()
    const upperShorthand = q.toUpperCase()
    result = result.filter((c) => {
      if (c.title.toLowerCase().includes(lower)) return true
      if (c.shorthand && c.shorthand === upperShorthand) return true
      return false
    })
  }

  return result
}

/**
 * Build the union of distinct assignees that appear across the visible
 * card set. The dashboard payload doesn't carry per-project member
 * rosters, so we derive the picker options from the cards themselves —
 * any assignee currently on a card is offerable as a filter option.
 */
export interface MasterAssigneeOption {
  userId: string
  name: string
  avatarUrl: string | null
}

export function deriveMasterAssigneeOptions(
  cards: readonly DashboardCard[],
): MasterAssigneeOption[] {
  const seen = new Map<string, MasterAssigneeOption>()
  for (const c of cards) {
    if (c.assigneeId && c.assigneeName) {
      if (!seen.has(c.assigneeId)) {
        seen.set(c.assigneeId, {
          userId: c.assigneeId,
          name: c.assigneeName,
          avatarUrl: c.assigneeAvatarUrl ?? null,
        })
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Cross-project label union (by name). One label option per distinct
 * name; uses the first-seen color so the chip preview in the dropdown
 * matches at least one of the projects' renderings.
 */
export interface MasterLabelOption {
  name: string
  color: string
}

export function deriveMasterLabelOptions(cards: readonly DashboardCard[]): MasterLabelOption[] {
  const seen = new Map<string, MasterLabelOption>()
  for (const c of cards) {
    for (const label of c.labels ?? []) {
      if (!seen.has(label.name)) {
        seen.set(label.name, { name: label.name, color: label.color })
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Attendee union across visible event/announcement cards.
 */
export interface MasterAttendeeOption {
  userId: string
  name: string
  avatarUrl: string | null
}

export function deriveMasterAttendeeOptions(
  cards: readonly DashboardCard[],
): MasterAttendeeOption[] {
  const seen = new Map<string, MasterAttendeeOption>()
  for (const c of cards) {
    for (const a of c.attendees ?? []) {
      if (!seen.has(a.userId)) {
        seen.set(a.userId, { userId: a.userId, name: a.name, avatarUrl: a.avatarUrl ?? null })
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}
